<template>
  <Layout>
    <div class="ecu-apply memberStep">
      <div v-if="isLoading" class="preloader-container">
        <div class="preloader"></div>
      </div>
      <div v-else>
      <div
        v-if="showErrorMessage"
        class="ea-alert ea-alert--error"
        role="alert"
      >
        Please Fill All The Required Field
      </div>
      <div v-if="saveErrorText" class="ea-alert ea-alert--error" role="alert">
        {{ saveErrorText }}
      </div>

      <!-- Step 0: membership type + residency -->
      <section v-if="!showRadio" class="ea-select">
        <div class="ea-select__card">
          <div class="ea-hero">
            <span class="ea-hero__tile">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /><path d="M6 20v-1a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1" /><path d="M5.5 10.5a2.5 2.5 0 1 1 1.2-4.7" /><path d="M3 17.5v-.7a3.5 3.5 0 0 1 2.6-3.4" /><path d="M18.5 10.5a2.5 2.5 0 1 0-1.2-4.7" /><path d="M21 17.5v-.7a3.5 3.5 0 0 0-2.6-3.4" /></svg>
            </span>
            <h1>Membership <span>Application</span></h1>
            <p>
              Let's get you started. Please select the type of membership and
              your residency.
            </p>
            <span class="ea-hero__bar"></span>
          </div>

          <div
            v-if="hide_entering_message"
            class="ea-alert ea-alert--ok mt-4 mb-0"
            role="status"
          >
            Welcome. Lets get started with your application
          </div>

          <div class="ea-residency">
            <span class="ea-residency__label">
              Residency
            </span>
            <div class="residency-toggle" role="group" aria-label="Residency">
              <button
                type="button"
                class="residency-opt"
                :class="{ active: residencyType === 'local' }"
                @click="selectResidency('local')"
              >
                Local
              </button>
              <button
                type="button"
                class="residency-opt"
                :class="{ active: residencyType === 'foreigner' }"
                @click="selectResidency('foreigner')"
              >
                Foreigner
              </button>
            </div>
          </div>
          <div v-if="residencyError" class="ea-field-error">
            Please select Local or Foreigner.
          </div>

          <div class="ea-types">
            <label
              for="adultRadio"
              class="ea-type"
              :class="{ 'is-selected': radioSelected1 === 'adult' }"
            >
              <input
                type="radio"
                v-model="radioSelected1"
                value="adult"
                name="radioAdult"
                @change="radioError = false"
                class="ea-type__input"
                id="adultRadio"
              />
              <span class="ea-type__radio" aria-hidden="true"></span>
              <span class="ea-type__icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" /><path d="M4.5 21v-1a6 6 0 0 1 6-6h3a6 6 0 0 1 6 6v1" /></svg>
              </span>
              <span class="ea-type__text">
                <strong>Adult</strong>
                <span>
                  Any person 18 years and over at the time of application is
                  considered an adult.
                </span>
              </span>
            </label>
            <label
              for="minorRadio"
              class="ea-type"
              :class="{ 'is-selected': radioSelected1 === 'minor' }"
            >
              <input
                type="radio"
                v-model="radioSelected1"
                value="minor"
                name="radioAdult"
                @change="radioError = false"
                class="ea-type__input"
                id="minorRadio"
              />
              <span class="ea-type__radio" aria-hidden="true"></span>
              <span class="ea-type__icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /><path d="M2.5 20v-.5A5.5 5.5 0 0 1 8 14h2a5.5 5.5 0 0 1 5.5 5.5v.5" /><path d="M16.5 11a2.7 2.7 0 1 0 0-5.3" /><path d="M18.5 14.3a4.5 4.5 0 0 1 3 4.2V20" /></svg>
              </span>
              <span class="ea-type__text">
                <strong>Minor</strong>
                <span>
                  Any person under the age of 18 at the time of application is
                  considered a minor.
                </span>
              </span>
            </label>
          </div>
          <div
            v-if="radioError"
            class="ea-field-error ea-field-error--left"
            role="alert"
          >
            Please select Adult or Minor.
          </div>

          <div class="ea-select__actions">
            <button
              @click="showRadio = true"
              ref="anotherButton"
              type="button"
              style="display: none"
              class="btn btn-theme btn-custom-wdth"
            >
              Next
            </button>
            <button
              @click="submitDataInitial"
              type="button"
              class="btn btn-theme btn-custom-wdth ea-btn"
            >
              Next
              <AppIcon name="arrow-right" :size="18" :stroke="2.2" />
            </button>
          </div>
        </div>
      </section>

      <!-- Steps 1..n -->
      <section v-else class="ea-flow">
        <header class="ea-flow__head">
          <h1>Membership <span>Application</span></h1>
          <p>Apply for membership with Eastern Credit Union in a few simple steps.</p>
        </header>
        <form-wizard
          ref="wizard"
          @on-complete="onComplete"
          :start-index="startIndex"
          step-size="xs"
          color="#f97316"
          :style="{ '--ea-steps': radioSelected1 === 'adult' ? 7 : 8 }"
          @on-change="onStepChanged"
          @saveFormData="saveFormData"
        >
          <tab-content
            class="tab_1"
            title="Personal Information"
            :before-change="handleBeforeChange"
          >
            <PersonalInfoStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_2"
            title="Employment Details"
            :before-change="handleBeforeChange1"
          >
            <EmploymentStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_3"
            title="Account Activity"
            :before-change="handleBeforeChange2"
          >
            <AccountActivityStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_4"
            title="Guardian Details"
            :before-change="handleBeforeChange3"
            v-if="radioSelected1 !== 'adult'"
          >
            <GuardianStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_5"
            title="PEP Declaration"
            :before-change="handleBeforeChange4"
          >
            <PepStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_6"
            title="FATCA"
            :before-change="handleBeforeChange5"
          >
            <FatcaStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_8"
            title="Nominee"
            :before-change="handleBeforeChange7"
          >
            <NomineeStep mode="create" />
          </tab-content>
          <tab-content
            class="tab_9"
            title="Declaration"
            :before-change="handleBeforeChange8"
          >
            <DeclarationStep mode="create" />
          </tab-content>
          <template #custom-buttons-left="{ activeTabIndex }">
            <button
              v-if="activeTabIndex === 0"
              type="button"
              class="wizard-btn"
              @click="resetFields()"
            >
              Back
            </button>
          </template>
          <template #custom-buttons-right="{ activeTabIndex }">
            <span class="ea-step-count">
              Step {{ activeTabIndex + 1 }} of {{ radioSelected1 === 'adult' ? 7 : 8 }}
            </span>
          </template>
        </form-wizard>
      </section>
      </div>
    </div>
  </Layout>
</template>
<style>
/* number inputs: no spinner arrows */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
<script>
import storageService from "@/services/storage.service";
import AppIcon from "@/components/shared/AppIcon.vue";
import "@/assets/css/ecu-apply.css";
import Layout from "../../layouts/applicant";
import { FormWizard, TabContent } from "vue3-form-wizard";
import "vue3-form-wizard/dist/style.css";
import {
  PersonalInfoStep,
  EmploymentStep,
  AccountActivityStep,
  GuardianStep,
  PepStep,
  FatcaStep,
  NomineeStep,
  DeclarationStep,
} from "@/components/forms/steps";
import { provideFormContext } from "@/components/forms/formBridge";
import axios from "axios";
import useVuelidate from "@vuelidate/core";
import { required, minLength, helpers } from "@vuelidate/validators";
import {
  YEAR_CHOICES,
  MONTH_CHOICES,
  TITLE_CHOICES,
  RELATIONSHIP_CHOICES,
  INCOME_RANGES,
  INCOME_SOURCE_CHOICES,
  FUNDING_SOURCE_CHOICES,
  BRANCH_CHOICES,
  COUNTRY_CHOICES,
  PHONE_COUNTRY_CODE_CHOICES,
  DOCUMENT_ISSUANCE_COUNTRIES,
} from "@/constants";
import applicantFormShared from "@/mixins/applicantFormShared";

export default {
  mixins: [applicantFormShared],
  props: {
    value: String,
  },

  // Step components (components/forms/steps) reach this view through formBridge.
  provide: provideFormContext,

  setup() {
    return { v$: useVuelidate() };
  },

  mounted() {
    setTimeout(() => {
      this.hide_entering_message = false;
    }, 4000);
    this.updateParentValue();
    this.fetchData();
    this.incSlab();
    this.usermail = storageService.getItem("userEmail");

  },

  data() {
    return {
      // Flags set dynamically by the validators below; declared so step components can read them.
      invalidDOBadult: false,
      ispassIssuenumberInvalid: false,
      ispassnumberInvalid: false,
      ispassnumberInvalid2: false,
      ispassnumberInvalid5: false,
      ispassnumberInvalid10: false,
      residencyType: "local", // Local is pre-selected
      residencyError: false,
      radioError: false,
      saveErrorText: "",
      saveErrorTimer: null,
      showErrorMessage: false,
      hide_entering_message: true,
      errorpartone: false,
      errorpart: false,
      errorparttwo: false,
      selectedYearMdl: "",
      selectedMonthMdl: "",
      selectedYearOpt: YEAR_CHOICES,
      selectedMonthOpt: MONTH_CHOICES,
      maxLength: 11,
      selectedCountryCode: "", // Initially selected country code
      phoneNumber: this.value || "", // Initially entered phone number
      selectedFile1: null,
      selectedFile2: null,
      selectedFile3: null,
      selectedFile4: null,
      selectedFile5: null,
      selectedFile6: null,
      selectedFile7: null,
      selectedFile8: null,
      selectedFile9: [],
      fileSizeError9: "",
      usermail: "",
      optionsList: [],
      startIndex: "",
      app_id: "",
      isLoading: false,
      radioSelected1: "",
      invalidDOBMinor: false,
      backendValue: "",
      checkFeildInput1: "",
      checkFeildInput2: "",
      riskInput: "",
      app_URL: process.env.APP_URL,
      showValue: true,
      fatcaFeild: "",
      checkFeildInput: [],
      radioSelected: "",
      trustee_id: "",
      gurdian_id: "",
      showRadio: false,
      preview: [],
      image: [],
      averageIncmOpt: INCOME_RANGES,
      nationalityOpt: COUNTRY_CHOICES,
      country_nameOpt: COUNTRY_CHOICES,
      titleNameOpt: TITLE_CHOICES,
      relationNameOpt: RELATIONSHIP_CHOICES,
      branchNameOpt: BRANCH_CHOICES,
      fundedoption: FUNDING_SOURCE_CHOICES,
      Incomeoption: INCOME_SOURCE_CHOICES,
      countrynameIssuanceOpt: PHONE_COUNTRY_CODE_CHOICES,
      nationalIssuanceOpt: DOCUMENT_ISSUANCE_COUNTRIES,
      showMessageAfterClearingExpiry: false,
      showMessageAfterClearing: false,
      showMessageAfterClearingdid: false,
      isInvalidDOB: false,
      isInvalidDOB1: false,
      isInvalidDOB2: false,
      isInvalidDOB3: false,
      form: {
        personal1: {
          branchName: "",
          title: "",
          surname: "",
          firstName: "",
          address_line: "",
          address_line_two: "",
          city_name: "",
          state_name: "",
          zip_code: "",
          country_name: "Trinidad and Tobago",
          dob: "",
          gender: "",
          numOfDependents: "",
          placeOfdob: "",
          nationality: "Trinidad and Tobago",
          maritalStatus: "",
          nationalIssuance: "Trinidad and Tobago",
          driverIssuance: "Trinidad and Tobago",
          passportIssuance: "Trinidad and Tobago",
          birthIdNum: "",
          birthIssuance: "Trinidad and Tobago",
          homePhoneNum: "",
          homePhoneNumcode: "+868-TTO",
          mobileNumcode: "+868-TTO",
          mobileNum: "",
          email: "",
        },
        personal2: {
          employStatus: "",
          frequencyStatus: "",
          averageIncm: "",
          user_occupation: "",
          employer: "",
          user_employer: "",
          user_w_phone_no: "",
          user_w_phone_nocode: "+868-TTO",
          user_phone_no: "",
          user_isstudent: "",
          user_howlong: "",
          user_howlong2: "",
        },
        personal3: {
          accFunded: "",
          accountActi: "",
          numTrans: "",
          areaofdetails: "",
          volTrans: "",
        },
        personal4: {
          parentName: "",
          parentId: "",
          trusteeName: "",
          trusteeId: "",
          advisor: "",
        },
        personal5: {
          entrustedRadio: "",
          memberOfRadio: "",
          referredRadio: "",
        },
        personal6: {
          tobagoRadio: "",
          citizenRadio: "",
          ssn_no: "",
          addressRadio: "",
          incomeUsRadio: "",
        },
        personal7: {
          account_one: "",
          shares_amount: "",
          account_two: "",
          special_shares_amount: "",
          account_three: "",
          deposit_ac_amount: "",
          reg_fee_amount: "",
          total_amount: "",
        },
        personal8: {
          appointnominee_name1: "",
          appointnominee_email1: "",
          appointnominee_pno1: "",
          appointnominee_pno1code: "+868-TTO",
          appointnominee_otherel: "",
          appointnominee_rel1: "",
          appointnominee_address1: "",
          appointnominee_dob1: "",
          appointnominee_name2: "",
          appointnominee_email2: "",
          appointnominee_pno2: "",
          appointnominee_pno2code: "+868-TTO",
          appointnominee_rel2: "",
          appointnominee_othere2: "",
          appointnominee_address2: "",
          appointnominee_dob2: "",
          hereRadioType: true,
          trusteeRadioo: "",
          appointnominee_othere3: "",
          appointnominee_name3: "",
          appointnominee_pno3code: "+868-TTO",
          appointnominee_pno3: "",
          appointnominee_email3: "",
          appointnominee_dob3: "",
          appointnominee_address3: "",
        },
        personal9: {
          hereRadioType_one: "",
        },
      },
    };
  },
  

  validations: {
    form: {
      personal1: {
        branchName: {
          required: helpers.withMessage("Branch Name is required...", required),
        },
        title: {
          required: helpers.withMessage("Member Name is required...", required),
        },
        surname: {
          required: helpers.withMessage("Surname is required...", required),
        },
        firstName: {
          required: helpers.withMessage("First Name is required...", required),
        },
        // middleName: {
        address_line: {
          required: helpers.withMessage("Address is required...", required),
        },
        city_name: {
          required: helpers.withMessage("City Name is required...", required),
        },
        country_name: {
          required: helpers.withMessage(
            "Country Name is required...",
            required,
          ),
        },
        maritalStatus: {
          required: helpers.withMessage("Marital is required...", required),
        },
        dob: {
          required: helpers.withMessage(
            "Date of Birth is required...",
            required,
          ),
        },
        gender: {
          required: helpers.withMessage("Gender is required...", required),
        },
        numOfDependents: {
          required: helpers.withMessage("Dependents is required...", required),
        },
        placeOfdob: {
          required: helpers.withMessage("Place is required...", required),
        },
        nationality: {
          required: helpers.withMessage("Nationality is required...", required),
        },
        homePhoneNum: {
          required: helpers.withMessage(
            "Home Phone No is required...",
            required,
          ),
          minLength: helpers.withMessage(
            "Mobile No should be at least 7 characters long...",
            minLength(7),
          ),
        },
        homePhoneNumcode: {
          required: helpers.withMessage("Required...", required),
        },
        email: {
          required: helpers.withMessage(
            "Make Sure Email is Correct...",
            required,
          ),
          // email: helpers.withMessage("Enter a valid Email", email),
        },
      },
      personal2: {
        // -------------------------------------------section 2-----------------
        employStatus: {
          required: helpers.withMessage("Required...", required),
        },
        frequencyStatus: {
          required: helpers.withMessage("Required...", required),
        },
        user_howlong: {
          required: helpers.withMessage("Month is required...", required),
        },
        user_howlong2: {
          required: helpers.withMessage("Year is required...", required),
        },
        averageIncm: {
          required: helpers.withMessage("Required...", required),
        },
        user_occupation: {
          required: helpers.withMessage("Occupation is required...", required),
        },
        employer: {
          required: helpers.withMessage("Employer is required...", required),
        },
        user_employer: {
          required: helpers.withMessage(
            "Employer address is required...",
            required,
          ),
        },
        user_w_phone_no: {
          required: helpers.withMessage("Phone is required...", required),
          minLength: helpers.withMessage(
            "Should be at least 7 characters long...",
            minLength(7),
          ),
        },
        user_w_phone_nocode: {
          required: helpers.withMessage("Required...", required),
        },
      },
      personal3: {
        // ----------------------section 3-------------------------------------------------------------------------------
        accFunded: {
          required: helpers.withMessage("Fund is required...", required),
        },
        accountActi: {
          required: helpers.withMessage("This is required...", required),
        },
        areaofdetails: {
          required: helpers.withMessage("Details is required...", required),
        },
        numTrans: {
          required: helpers.withMessage("Transaction is required...", required),
        },
        volTrans: {
          required: helpers.withMessage("Transaction is required...", required),
        },
      },
      personal4: {
        // -------------------section 4-----------MINOR------------------
        parentName: {
          required: helpers.withMessage("Parent Name is required...", required),
        },
        parentId: {
          required: helpers.withMessage("Parent Id is required...", required),
        },
        trusteeName: {
          required: helpers.withMessage(
            "Trustee Name is required...",
            required,
          ),
        },
        trusteeId: {
          required: helpers.withMessage("Trustee id is required...", required),
        },
      },
      personal5: {
        // ------------------SECTION  5------------PEP-------------
        entrustedRadio: {
          required: helpers.withMessage("Required...", required),
        },
        memberOfRadio: {
          required: helpers.withMessage("Required...", required),
        },
        referredRadio: {
          required: helpers.withMessage("Required...", required),
        },
      },
      personal6: {
        // -----------------SECTION 6 --------FATCA----------
        tobagoRadio: {
          required: helpers.withMessage("Required...", required),
        },
        citizenRadio: {
          required: helpers.withMessage("Required...", required),
        },
        ssn_no: {
          required: helpers.withMessage("SSN no is  required...", required),
        },
        addressRadio: {
          required: helpers.withMessage("Required...", required),
        },
        incomeUsRadio: {
          required: helpers.withMessage("Required...", required),
        },
      },
      personal7: {
        account_one: {
          required: helpers.withMessage(
            "Account Number is required...",
            required,
          ),
        },
        account_two: {
          required: helpers.withMessage(
            "Account Number is required...",
            required,
          ),
        },
        account_three: {
          required: helpers.withMessage(
            "Account Number is required...",
            required,
          ),
        },
        shares_amount: {
          required: helpers.withMessage("Amount is required...", required),
        },
        special_shares_amount: {
          required: helpers.withMessage("Amount is required...", required),
        },
        deposit_ac_amount: {
          required: helpers.withMessage("Amount is required...", required),
        },
        reg_fee_amount: {
          required: helpers.withMessage("Amount is required...", required),
        },
        total_amount: {
          required: helpers.withMessage("Amount is required...", required),
        },
      },
      personal8: {
        // // appointment of nominee-----section 8--------------
        appointnominee_name1: {
          required: helpers.withMessage("Name is required...", required),
        },
        appointnominee_email1: {
          required: helpers.withMessage("Email is required...", required),
        },
        appointnominee_pno1: {
          required: helpers.withMessage("Phone No is required...", required),
          minLength: helpers.withMessage(
            "Mobile No should be at least 7 characters long...",
            minLength(7),
          ),
        },
        appointnominee_pno1code: {
          required: helpers.withMessage("Required...", required),
        },
        appointnominee_rel1: {
          required: helpers.withMessage("Relation is required...", required),
        },
        appointnominee_address1: {
          required: helpers.withMessage("Address is required...", required),
        },
        appointnominee_dob1: {
          required: helpers.withMessage(
            "Date of birth is required...",
            required,
          ),
        },

      },
      personal9: {
        hereRadioType_one: {
          required: helpers.withMessage("Required...", required),
        },
      },
    },
  },
  components: {
    FormWizard,
    TabContent,
    Layout,
    AppIcon,
    PersonalInfoStep,
    EmploymentStep,
    AccountActivityStep,
    GuardianStep,
    PepStep,
    FatcaStep,
    NomineeStep,
    DeclarationStep,
  },

  methods: {
    isPdf(type) {
      return type === "pdf";
    },
    validatehomenumber() {
      if (
        this.form.personal1.homePhoneNum !== null &&
        this.form.personal1.homePhoneNum.toString().length < 7
      ) {
        this.ispassIssuenumberInvalid = true;
      } else {
        this.ispassIssuenumberInvalid = false;
        if (this.form.personal1.homePhoneNum !== null) {
          this.form.personal1.homePhoneNum = this.form.personal1.homePhoneNum
            .toString()
            .slice(0, 13);
        }
      }
    },
    validatemobnumber() {
      if (
        this.form.personal1.mobileNum !== null &&
        this.form.personal1.mobileNum.toString().length < 7
      ) {
        this.ispassnumberInvalid = true;
      } else {
        this.ispassnumberInvalid = false;
        if (this.form.personal1.mobileNum !== null) {
          this.form.personal1.mobileNum = this.form.personal1.mobileNum
            .toString()
            .slice(0, 13);
        }
      }
    },
    validatemobnumber4() {
      if (
        this.form.personal8.appointnominee_pno2 !== null &&
        this.form.personal8.appointnominee_pno2.toString().length < 7
      ) {
        this.ispassnumberInvalid = true;
      } else {
        this.ispassnumberInvalid = false;
        if (this.form.personal8.appointnominee_pno2 !== null) {
          this.form.personal8.appointnominee_pno2 =
            this.form.personal8.appointnominee_pno2.toString().slice(0, 13);
        }
      }
    },
    validatemobnumber6() {
      if (
        this.form.personal8.relationbenifit3 !== null &&
        this.form.personal8.relationbenifit3.toString().length < 7
      ) {
        this.ispassnumberInvalid = true;
      } else {
        this.ispassnumberInvalid = false;
        if (this.form.personal8.relationbenifit3 !== null) {
          this.form.personal8.relationbenifit3 =
            this.form.personal8.relationbenifit3.toString().slice(0, 13);
        }
      }
    },
    validatemobnumber5() {
      if (
        this.form.personal8.appointnominee_pno1 !== null &&
        this.form.personal8.appointnominee_pno1.toString().length < 7
      ) {
        this.ispassnumberInvalid5 = true;
      } else {
        this.ispassnumberInvalid5 = false;
        if (this.form.personal8.appointnominee_pno1 !== null) {
          this.form.personal8.appointnominee_pno1 =
            this.form.personal8.appointnominee_pno1.toString().slice(0, 13);
        }
      }
    },
    validatemobnumber10() {
      if (
        this.form.personal8.appointnominee_pno3 !== null &&
        this.form.personal8.appointnominee_pno3.toString().length < 7
      ) {
        this.ispassnumberInvalid10 = true;
      } else {
        this.ispassnumberInvalid10 = false;
        if (this.form.personal8.appointnominee_pno3 !== null) {
          this.form.personal8.appointnominee_pno3 =
            this.form.personal8.appointnominee_pno3.toString().slice(0, 13);
        }
      }
    },
    validatemobnumber2() {
      if (
        this.form.personal2.user_w_phone_no !== null &&
        this.form.personal2.user_w_phone_no.toString().length < 7
      ) {
        this.ispassnumberInvalid = true;
      } else {
        this.ispassnumberInvalid = false;
        if (this.form.personal2.user_w_phone_no !== null) {
          this.form.personal2.user_w_phone_no =
            this.form.personal2.user_w_phone_no.toString().slice(0, 13);
        }
      }
    },
    validatemobnumber3() {
      if (
        this.form.personal2.user_phone_no !== null &&
        this.form.personal2.user_phone_no.toString().length < 7
      ) {
        this.ispassnumberInvalid2 = true;
      } else {
        this.ispassnumberInvalid2 = false;
        if (this.form.personal2.user_phone_no !== null) {
          this.form.personal2.user_phone_no = this.form.personal2.user_phone_no
            .toString()
            .slice(0, 13);
        }
      }
    },
    validateDrivingIdNum() {
      if (this.form.personal1.drivingIdNum !== null) {
        // Limit to 6 digits
        this.form.personal1.drivingIdNum = this.form.personal1.drivingIdNum
          .toString()
          .slice(0, 13);
      }
    },

    //   // Form submission logic

    // -------------------------------------------SECTION STEPS
    async handleBeforeChange() {
      let v = this.validateFormOne();

      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 5000); // Hide after 5 seconds
      }
      let sdp;
      let i = this.threeFieldcheck();
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataPersonal();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true && i === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange1() {
      let v = this.validateFormTwo();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }

      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataEmployement();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange2() {
      let v = this.validateFormThree();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }
      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataAccount();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange3() {

      let v = this.validateFormFour();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }

      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataMinor();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange4() {
      let v = this.validateFormFive();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }

      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataPEP();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange5() {
      let v = this.validateFormsix();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }

      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitDataFATCA();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange6() {
      //this is for Account/Fees
      let v = this.validateFormSeven();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }

      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitData_accountandfees();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange7() {
      let v = this.validateFormEight();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }
      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitData_appointment_nominee();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },
    async handleBeforeChange8() {
      let v = this.validateFormNine();
      if (v === false) {
        this.showErrorMessage = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000); // Hide after 5 seconds
      }
      let sdp;
      try {
        if (v !== true) throw new Error("validation failed");
        await this.submitData_memberdec();
        sdp = true; // API call succeeded
      } catch (error) {
        sdp = false; // API call failed
      }

      // Call `next()` to proceed with the navigation

      if (v === true && sdp === true) {
        return true;
      } else {
        return false;
      }
    },

    //   // Call the second function

    //   // Call `next()` to proceed with the navigation

    // -------------------------------------SECTION STEPS END

    // ------------------------------------VALIDATION START
    validateFormOne() {
      if (this.v$.form.personal1.$invalid) {
        this.v$.form.personal1.$touch();
        return false;
      } else {
        return true;
      }
    },
    //   validateFormFour() {
    validateFormFour() {
      if (this.v$.form.personal4.$invalid) {
        this.v$.form.personal4.$touch();
        if (
          this.form.personal4.parentName === "" &&
          this.form.personal4.parentId === "" &&
          this.form.personal4.trusteeName === "" &&
          this.form.personal4.trusteeId === ""
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    },
    previewImage(event, id) {
      var input = event.target;
      if (input.files) {
        var reader = new FileReader();
        reader.onload = (e) => {
          var data = {
            value: e.target.result,
            type: input.files[0].name.substring(
              input.files[0].name.lastIndexOf(".") + 1,
            ),
          };
          this.preview[id] = data;
        };
        this.image[id] = input.files[0];
        reader.readAsDataURL(input.files[0]);
      }
    },

    // Shows why a step could not be saved. The wizard stays on the current tab.

    fetchData() {
      var t = storageService.getItem("accessToken");
      this.isLoading = true;
      axios
        .post("/applications/applicant_app_status/", { token: t })
        .then((response) => {
          const app = response.data.app_data;
          const full = response.data.app_full;
          if (app) {
            this.app_id = app.id;
            this.radioSelected1 = app.is_adult === true ? "adult" : "minor";
            this.checkFeildInput2 = app.is_fatca === true;
            this.checkFeildInput1 = app.is_pep === true;
            if (app.risk_cat) this.riskInput = app.risk_cat;
          }
          this.residencyType = (app && app.residency_type) || "local";
          this.form.personal1.email = this.usermail;
          // refill every saved field, then open the first step that is not saved yet
          this.applyServerData(full);
          this.startIndex = app ? this.computeStartIndex(full) : 0;
          this.showRadio = !!app;
          this.isLoading = false;
          this.markStepsDone(this.startIndex);
        })
        .catch((error) => {
          console.error("yes i m error ", error);
        });
    },
    submitDataPersonal() {
      var t = storageService.getItem("accessToken");
      let r = false;
      this.form.personal9.appliname = `${this.form.personal1.firstName} ${this.form.personal1.surname}`;
      const formData = new FormData();
      var formdata1 = {
        national_data: this.selectedFile1,
        utility_data: this.selectedFile4,
        token: t,
        app_id: this.app_id,
        branch: this.form.personal1.branchName,
        title: this.form.personal1.title,
        surname: this.form.personal1.surname,
        first_name: this.form.personal1.firstName,
        middle_name: this.form.personal1.middleName,
        home_add: this.form.personal1.address,
        address_line: this.form.personal1.address_line,
        address_line_two: this.form.personal1.address_line_two,
        city_name: this.form.personal1.city_name,
        state_name: this.form.personal1.state_name,
        zip_code: this.form.personal1.zip_code,
        country_name: this.form.personal1.country_name,
        dob: this.form.personal1.dob,
        gender: this.form.personal1.gender,
        dependents: this.form.personal1.numOfDependents,
        place_of_birth: this.form.personal1.placeOfdob,
        nationality: this.form.personal1.nationality,
        marital_status: this.form.personal1.maritalStatus,
        // National ID section
        national_id: this.form.personal1.nationalIdNum,
        national_id_issue_date: this.form.personal1.nidIssueDate,
        national_id_expiry_date: this.form.personal1.nidExpiry,
        national_id_country: this.form.personal1.nationalIssuance,
        national_id_doc: this.selectedFile1,
        // Driving License Section
        driver_permit: this.form.personal1.drivingIdNum,
        driver_permit_issue_date: this.form.personal1.didIssueDate,
        driver_permit_expiry_date: this.form.personal1.didExpiry,
        driver_permit_country: this.form.personal1.driverIssuance,
        driver_permit_doc: this.selectedFile2,
        // Passport Section
        passport_no: this.form.personal1.passportNum,
        passport_no_issue_date: this.form.personal1.passIssueDate,
        passport_no_expiry_date: this.form.personal1.passExpiry,
        passport_no_country: this.form.personal1.passportIssuance,
        passport_no_doc: this.selectedFile3,
        // DOB certificate Section
        dob_no: this.form.personal1.birthIdNum,
        dob_issue_country: this.form.personal1.birthIssuance,
        dob_doc: this.selectedFile5,

        home_mob_no: this.form.personal1.homePhoneNum,
        home_mob_no_code: this.form.personal1.homePhoneNumcode,
        mob_no: this.form.personal1.mobileNum,
        mob_no_code: this.form.personal1.mobileNumcode,
        email: this.form.personal1.email,
      };
      this.selectedFile9.forEach((file) => {
        formData.append("pay_slip", file);
      });

      Object.keys(formdata1).forEach((key) => {
        if (formdata1[key] === null || formdata1[key] === undefined) return;
        formData.append(key, formdata1[key]);
      });

      return axios
        .post("/applications/personal_information_submit/", formData)
        .then((response) => {
          this.afterPersonalSaved(response);
          // Handle the response
          this.form.personal9.appliname = `${this.form.personal1.firstName} ${this.form.personal1.surname}`;
          r = true;
          return r;
        })
        .catch((error) => {
          // Handle any errors
          console.error("Error sending email:", error);
          this.notifySaveError(error);
          throw error; // makes the tab change handler stay on this tab
        });

    },

    //     })

    submitDataMinor() {
      var t = storageService.getItem("accessToken");
      let r = false;
      this.form.personal9.nameofparent = this.form.personal4.parentName;
      this.form.personal9.nameoftrustee = this.form.personal4.trusteeName;
      return axios
        .post("/applications/minors_only_submit/", {
          token: t,
          app_id: this.app_id,
          parent_name: this.form.personal4.parentName,
          parent_id_no: this.form.personal4.parentId,
          trustee: this.form.personal4.trusteeName,
          trustee_id_no: this.form.personal4.trusteeId,
          advisor: this.form.personal4.advisor,
          // witness:
        })
        .then((response) => {
          // Handle the response
          this.trustee_id = response.data.section_d4.incare_g.id;
          this.gurdian_id = response.data.section_d4.incare_t.id;
          r = true;
          return r;
        })
        .catch((error) => {
          // Handle any errors
          console.error("Error sending email:", error);
          this.notifySaveError(error);
          throw error; // makes the tab change handler stay on this tab
        });
    },
    submitData_appointment_nominee() {
      var t = storageService.getItem("accessToken");
      let r = false;
      return axios
        .post("/applications/appointment_of_nominee/", {
          token: t,
          app_id: this.app_id,

          appointnominee_name1: this.form.personal8.appointnominee_name1,
          appointnominee_pno1: this.form.personal8.appointnominee_pno1,
          appointnominee_pno1code: this.form.personal8.appointnominee_pno1code,
          appointnominee_rel1: this.form.personal8.appointnominee_rel1,
          appointnominee_otherel: this.form.personal8.appointnominee_otherel,
          appointnominee_address1: this.form.personal8.appointnominee_address1,
          appointnominee_dob1: this.form.personal8.appointnominee_dob1,
          appointnominee_email1: this.form.personal8.appointnominee_email1,

          appointnominee_name2: this.form.personal8.appointnominee_name2,
          appointnominee_pno2: this.form.personal8.appointnominee_pno2,
          appointnominee_pno2code: this.form.personal8.appointnominee_pno2code,
          appointnominee_rel2: this.form.personal8.appointnominee_rel2,
          appointnominee_othere2: this.form.personal8.appointnominee_othere2,
          appointnominee_address2: this.form.personal8.appointnominee_address2,
          appointnominee_dob2: this.form.personal8.appointnominee_dob2,
          appointnominee_email2: this.form.personal8.appointnominee_email2,

          hereRadioType: this.form.personal8.hereRadioType,
          trusteeRadioo: this.form.personal8.trusteeRadioo,

          relationbenifit3: this.form.personal8.relationbenifit3,
          appointnominee_othere3: this.form.personal8.appointnominee_othere3,
          appointnominee_name3: this.form.personal8.appointnominee_name3,
          appointnominee_email3: this.form.personal8.appointnominee_email3,
          appointnominee_pno3code: this.form.personal8.appointnominee_pno3code,
          appointnominee_pno3: this.form.personal8.appointnominee_pno3,
          appointnominee_dob3: this.form.personal8.appointnominee_dob3,
          appointnominee_address3: this.form.personal8.appointnominee_address3,
        })
        .then(() => {
          // Handle the response
          r = true;
          return r;
        })
        .catch((error) => {
          // Handle any errors
          console.error("Error sending email:", error);
          this.notifySaveError(error);
          throw error; // makes the tab change handler stay on this tab
        });
    },
    submitData_memberdec() {
      var t = storageService.getItem("accessToken");
      let r = false;

      const formData = new FormData();
      var formdata1 = {
        token: t,
        app_id: this.app_id,
        hereRadioType_one: this.form.personal9.hereRadioType_one,
        datename8: this.form.personal9.datename8,
        applicant_sign: this.selectedFile6,
        guardian_sign: this.selectedFile7,
        truste_sign: this.selectedFile8,

        parent_name: this.form.personal4.parentName,
        parent_id_no: this.form.personal4.parentId,
        trustee: this.form.personal4.trusteeName,
        trustee_id_no: this.form.personal4.trusteeId,
      };
      Object.keys(formdata1).forEach((key) => {
        if (formdata1[key] === null || formdata1[key] === undefined) return;
        formData.append(key, formdata1[key]);
      });

      return axios
        .post("/applications/members_declaration/", formData)

        //     hereRadioType_one: this.form.personal9.hereRadioType_one,
        .then(() => {
          // Handle the response
          r = true;
          return r;
        })
        .catch((error) => {
          // Handle any errors
          console.error("Error sending email:", error);
          this.notifySaveError(error);
          throw error; // makes the tab change handler stay on this tab
        });
    },

  },
};
</script>